import { _ } from '@zto/zpage'
import type { PageContext } from '@zto/zpage'

import { commonOptions, DialogType, EnableStatus, TdlDeviceSNStatus, TdlDeviceType, YesNo } from '@/config'
import { filterSubmitData, getCommonOptionLabel } from '@/utils'

// 新增/编辑对话框
export const addEditDialog = (dialogType: DialogType) => {
  const isEdit = dialogType === DialogType.EDIT
  const isAdd = dialogType === DialogType.ADD

  return ({ app }: PageContext) => {
    return {
      width: 600,
      labelWidth: 100,
      itemSpan: 24,

      onShow: async ({ data }: PageContext) => {
        const deviceTypeName = getCommonOptionLabel(commonOptions.tdlDeviceType, data.deviceType)

        if (data.snCode) {
          const res = await app.request({
            api: '/sys/admin/device/device-list/affiliated/list',
            params: { snCode: data.snCode }
          })

          const affiliatedList = res.affiliatedList || []
          data.centralSn = affiliatedList.find((it: any) => it.deviceType === TdlDeviceType.CENTER_CONTROLLER)?.snCode
          data.chassisSn = affiliatedList.find((it: any) => it.deviceType === TdlDeviceType.CHASSIS)?.snCode
          data.cargoSn = affiliatedList.find((it: any) => it.deviceType === TdlDeviceType.TRUNK)?.snCode
        }

        // 查询货仓格口模版信息
        if (data.cargoSn) {
          // 查询货仓对应的模板
          const res = await app.request({
            api: '/sys/admin/device/device-sn/detail',
            params: { snCode: data.cargoSn }
          })

          data.cellTemplateCode = res.cellTemplateCode
          data.cellTemplateName = res.cellTemplateName
        }

        const _oldData = _.deepClone(data)

        return {
          payload: { ...data, deviceTypeName, _oldData }
        }
      },

      beforeSubmit: async ({ data }: PageContext, model: any, options: any) => {
        const payload = filterSubmitData(data)
        payload.siteCode = data.siteCode || '' // 站点可选，但接口需要传''
        return payload
      },

      formItems: [
        {
          type: 'input',
          prop: 'snCode',
          label: '设备SN',
          trim: true,
          maxlength: 50,
          required: true,
          disabled: isEdit,
          span: 18
        },
        {
          label: false,
          type: 'action',
          actionLabel: '查询',
          span: 6,
          trigger: async (attrs: any, { app, data }: PageContext) => {
            const snInfo = await app.request({
              api: '/sys/admin/device/device-sn/detail',
              params: {
                deviceTypeCode: TdlDeviceType.VEHICLE,
                snCode: data.snCode
              }
            })

            if (isAdd) {
              if (!snInfo || snInfo.status === TdlDeviceSNStatus.VOIDED) {
                app.ui.showMessage({
                  type: 'warning',
                  message: '未查询到有效的整车SN ' + data.snCode
                })
                return
              }

              if (snInfo.status === TdlDeviceSNStatus.ACTIVATED) {
                app.ui.showMessage({
                  type: 'warning',
                  message: '当前SN已激活'
                })
                return
              }
            }

            data.deviceType = snInfo.deviceTypeCode
            data.deviceTypeName = snInfo.deviceTypeName
            data.modelName = snInfo.modelName
            data.manufacturerName = snInfo.manufacturerName
          }
        },
        { type: 'input', prop: 'name', label: '设备名称', maxlength: 20, trim: true, required: true },
        { type: 'input', prop: 'deviceTypeName', label: '设备类型', disabled: true },
        { type: 'input', prop: 'modelName', label: '型号', disabled: true },
        { type: 'input', prop: 'manufacturerName', label: '供货厂商', disabled: true },
        {
          type: 'fuzzy-select',
          label: '所属站点',
          prop: 'siteCode',
          labelProp: 'siteName',
          api: '/sys/admin/site/enablePage',
          keywordProp: 'keyWord',
          optionValueProp: 'siteCode',
          optionLabelProp: 'siteName',
          triggerFocus: true
        },
        {
          label: '中控SN',
          type: 'fuzzy-select',
          prop: 'centralSn',
          api: '/sys/admin/device/device-sn/listValid',
          apiParams: { deviceTypeCode: TdlDeviceType.CENTER_CONTROLLER, current: 1, size: 500 },
          optionValueProp: 'snCode',
          optionLabelProp: 'snCode',
          triggerFocus: true,
          disabled: isEdit,
          dynamicAttrs: ({ data }: PageContext) => {
            const visible = data.deviceType === TdlDeviceType.VEHICLE
            if (!visible) {
              data.centralSn = ''
            }
            return { visible, required: visible, disabled: isEdit && data._oldData?.centralSn }
          }
        },
        {
          label: '底盘SN',
          type: 'fuzzy-select',
          prop: 'chassisSn',
          api: '/sys/admin/device/device-sn/listValid',
          apiParams: { deviceTypeCode: TdlDeviceType.CHASSIS, current: 1, size: 500 },
          optionValueProp: 'snCode',
          optionLabelProp: 'snCode',
          triggerFocus: true,
          dynamicAttrs: ({ data }: PageContext) => {
            const visible = data.deviceType === TdlDeviceType.VEHICLE
            if (!visible) {
              data.chassisSn = ''
            }
            return { visible, required: visible, disabled: isEdit && data._oldData?.chassisSn }
          }
        },
        {
          label: '货仓SN',
          type: 'fuzzy-select',
          prop: 'cargoSn',
          api: '/sys/admin/device/device-sn/listValid',
          apiParams: { deviceTypeCode: TdlDeviceType.TRUNK, current: 1, size: 500 },
          optionValueProp: 'snCode',
          optionLabelProp: 'snCode',
          triggerFocus: true,
          onChange: async (data: any) => {
            // 查询货仓对应的模板
            const res = await app.request({
              api: '/sys/admin/device/device-sn/detail',
              params: { snCode: data.cargoSn }
            })

            data.cellTemplateCode = res.cellTemplateCode
            data.cellTemplateName = res.cellTemplateName
          },
          dynamicAttrs: ({ data }: PageContext) => {
            const visible = data.deviceType === TdlDeviceType.VEHICLE
            if (!visible) {
              data.cargoSn = ''
            }
            return { visible, required: visible, disabled: isEdit && data._oldData?.cargoSn }
          }
        },
        {
          label: '货仓格口模版',
          type: 'input',
          prop: 'cellTemplateName',
          disabled: true,
          dynamicAttrs: ({ data }: PageContext) => {
            const visible = !!data.cargoSn
            if (!visible) {
              data.cellTemplateCode = ''
              data.cellTemplateName = ''
            }
            return { visible }
          }
        },
        { type: 'textarea', prop: 'remark', label: '备注', maxlength: 200, showWordLimit: true }
      ]
    }
  }
}

const OperateType = Object.freeze({
  RECALL: {
    operationCode: 'CALL_BACK',
    label: '召回',
    notice: '召回指令将下发至整车聚合接口，车辆将停止配送并返回站点。',
    noticeStyle: 'color: orange; border: 1px solid #FDF0BE; background: #FEFBE6;'
  },
  REBOOT: {
    operationCode: 'REBOOT',
    label: '重启',
    notice: '重启可能导数车辆短暂离线、当前配送任务中断。请确认后再下发。',
    noticeStyle: 'color: blue; border: 1px solid #E6F7FF; background: #E6F7FF;'
  },
  STOP: {
    operationCode: 'STOP',
    label: '停止',
    notice: '停止指令用于紧急停车场景，请与召回、车载急停等能力边界区分清楚后再下发.',
    noticeStyle: 'color: red; border: 1px solid #FDE3E0; background: #FEF2F0;'
  }
})

const tdlDeviceOptReasonOptions = [
  {
    // label: '因续航/电量策略需召回、重启或停车处置等',
    value: '电量不足'
  },
  {
    // label: '软硬件异常、传感器/执行器异常等需人工介入场景',
    value: '设备故障'
  },
  {
    // label: '计划调整、站点指令、交通管制等运营侧原因',
    value: '运营调度'
  },
  {
    // label: '上述未覆盖场景；若选此项，需在备注中补充说明',
    value: '其他'
  }
]

export function operateDialog(operateType: 'RECALL' | 'REBOOT' | 'STOP') {
  const operationConfig = OperateType[operateType]

  return ({ app }: PageContext) => {
    return {
      width: 500,
      labelWidth: 80,
      itemSpan: 24,

      title: `确认${operationConfig.label}设备`,

      onShow: ({ data }: PageContext) => {
        return {
          payload: { snCode: data.snCode }
        }
      },

      onSubmit: async (data: any, options: any, dataModel: any, dialogExposed: any) => {
        await app.request({
          api: '/sys/admin/device/device-list/remote/send',
          params: {
            ...data,
            optType: operationConfig.operationCode
          }
        })

        dialogExposed.close()
      },

      formItems: [
        {
          labelWidth: 20,
          type: 'html',
          html: ({ data }: PageContext) => {
            return `<div>确定对整车${data.snCode}发送${operationConfig.label}指令吗？</div>`
          }
        },
        {
          type: 'select',
          prop: 'reason',
          label: '召回原因',
          maxlength: 100,
          options: tdlDeviceOptReasonOptions,
          required: true
        },
        {
          type: 'textarea',
          prop: 'remark',
          label: '备注',
          maxlength: 200,
          autoHeight: true,
          trim: true,
          dynamicAttrs: ({ data }: PageContext) => {
            const required = data.reason === '其他'
            return { required }
          }
        },
        {
          labelWidth: 20,
          type: 'html',
          autoHeight: true,
          html: `<div style='padding: 5px 10px; border-radius: 5px; line-height: 20px; ${operationConfig.noticeStyle}'>${operationConfig.notice}</div>`
        }
      ]
    }
  }
}

export function logDialog() {
  return {
    title: `操作日志`,

    width: 700,
    labelWidth: 0,
    itemSpan: 24,
    noPadding: true,

    bodyHeight: '400px',

    actions: [{ name: 'close', label: '关闭' }],

    body: ({ data }: PageContext) => {
      return {
        type: 'crud',
        actions: {
          query: {
            api: '/sys/admin/sys-config/queryOptLogList',
            apiParams: { module: 'DEVICE', snCode: data.snCode }
          }
        },
        header: {
          body: {
            type: 'html',
            html: `<div class="q-py-md" style="font-size: 12px;">设备${data.snCode}在平台上的关键操作记录（含添加、编辑、召回、重启、停止等）。</div>`
          }
        },
        toolbar: { hidden: true },
        search: { hidden: true },
        table: {
          columns: [
            { label: '时间', prop: 'gmtCreate', width: 150 },
            { label: '操作类型', prop: 'bizTypeName', width: 100 },
            { label: '操作人', prop: 'operatorName', width: 100 },
            {
              label: '结果',
              prop: 'status',
              formatter: {
                name: 'enumStr',
                options: [
                  { label: '成功', value: YesNo.YES },
                  { label: '失败', value: YesNo.NO }
                ]
              },
              width: 80
            },
            { label: '详情', prop: 'content', minWidth: 150 }
          ]
        }
      }
    }
  }
}

// formatter: { name: 'enumStr', options: commonOptions.tdlOptLogType },
